import {
  DefaultButton,
  Icon,
  IContextualMenuItem,
  IContextualMenuListProps,
  IContextualMenuProps,
  IRenderFunction,
  KeyCodes,
  PrimaryButton,
  SearchBox,
  Stack
} from "@fluentui/react";
import { useFilterOptions } from "@m365-admin/hooks";
import { FilterPill } from "@m365-admin/in-page-filter";
import { ChangeEvent, KeyboardEvent, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CustomFilterPillProps } from "./Interfaces/CustomFilterPill.interfaces";
import { CUSTOM_FILTER_PILL_STYLES as STYLES } from "./Styles/CustomFilterPill.styles";

export const CustomFilterPill = (props: CustomFilterPillProps) => {
  const menuItems = props.menuProps?.items || [];
  const containerRef = useRef<HTMLDivElement>(null);
  const [items, setItems] = useState<IContextualMenuItem[]>([]);
  const appliedFiltersRef = useRef<string[] | undefined>(undefined);
  const [appliedFilters, setAppliedFilters] = useState<string[] | undefined>(undefined);
  const [contextualMenuItem, setSelection, selectedKeys] = useFilterOptions(items.map(({ text }) => text ?? ""));
  const [searchParams, setSearchParams] = useSearchParams();

  const normalizeSingleSelection = useCallback(
    (values: string[] | undefined) => {
      if (!props.singleSelect || !values || values.length <= 1) return values;
      return [values[values.length - 1]];
    },
    [props.singleSelect]
  );

  // Set default useGroupedFilters to true
  const useGroupedFilters = props.useGroupedFilters ?? true;
  const groupedFiltersParamName = "filters";

  // Helper function to parse grouped filters from URL
  const parseGroupedFilters = useCallback(() => {
    const filtersParam = searchParams.get(groupedFiltersParamName);

    if (!filtersParam) return {};

    try {
      // Decode from base64 first, then parse JSON
      const decodedFilters = atob(filtersParam);
      return JSON.parse(decodedFilters);
    } catch {
      return {};
    }
  }, [searchParams, groupedFiltersParamName]);

  // Helper function to update grouped filters in URL
  const updateGroupedFilters = useCallback(
    (filterKey: string, values: string[] | undefined) => {
      if (!useGroupedFilters || !props.urlParamKey) return;

      const currentFilters = parseGroupedFilters();

      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);

        // Update the specific filter
        if (values && values.length > 0) {
          currentFilters[filterKey] = values.join(";");
        } else {
          delete currentFilters[filterKey];
        }

        // Update URL parameter
        if (Object.keys(currentFilters).length > 0) {
          // Encode to base64 instead of encodeURIComponent
          const encodedFilters = btoa(JSON.stringify(currentFilters));
          newParams.set(groupedFiltersParamName, encodedFilters);
        } else {
          newParams.delete(groupedFiltersParamName);
        }

        return newParams;
      });
    },
    [useGroupedFilters, props.urlParamKey, groupedFiltersParamName, parseGroupedFilters, setSearchParams]
  );

  // Regular URL parameter update (for non-grouped filters)
  const updateUrlParams = useCallback(
    (values: string[] | undefined) => {
      if (useGroupedFilters) {
        updateGroupedFilters(props.urlParamKey!, values);
        return;
      }

      if (!props.urlParamKey) return;

      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);

        if (values && values.length > 0) {
          newParams.set(props.urlParamKey!, values.join(";"));
        } else {
          newParams.delete(props.urlParamKey!);
        }

        return newParams;
      });
    },
    [useGroupedFilters, props.urlParamKey, setSearchParams, updateGroupedFilters]
  );

  // Initialize from URL params
  useEffect(() => {
    if (!props.urlParamKey) return;

    let urlValue: string | null = null;

    if (useGroupedFilters) {
      const groupedFilters = parseGroupedFilters();
      urlValue = groupedFilters[props.urlParamKey] || null;
    } else {
      urlValue = searchParams.get(props.urlParamKey);
    }

    if (urlValue) {
      const valuesArray = urlValue.split(";").filter((v) => v.trim() !== "");
      const normalizedValues = normalizeSingleSelection(valuesArray);

      // Convert to selection object
      const selectionObj = (normalizedValues ?? []).reduce(
        (acc, value) => {
          acc[value] = true;
          return acc;
        },
        {} as { [key: string]: boolean }
      );

      setSelection(selectionObj);
      appliedFiltersRef.current = normalizedValues;
      setAppliedFilters(normalizedValues);

      // Notify parent if onApply exists
      if (props.onApply) {
        props.onApply(normalizedValues);
      }
    }
  }, [props.urlParamKey, useGroupedFilters, parseGroupedFilters, normalizeSingleSelection]);

  const applyButtonDisabled =
    appliedFilters?.length === selectedKeys?.length &&
    appliedFilters?.every((value, index) => value === (selectedKeys || [])[index]);

  const onDismissVerifyResetMenuItems = (event?: Event | MouseEvent | KeyboardEvent, dismissAll?: boolean) => {
    const { current } = appliedFiltersRef;
    const applied = current ?? [];
    const isUnchangedState =
      applied.length === (selectedKeys?.length ?? 0) &&
      applied.every((value, index) => value === (selectedKeys || [])[index]);

    if (!isUnchangedState) {
      const oldStateData = applied.reduce((previousValue: any, currentValue) => {
        previousValue[currentValue] = true;
        return previousValue;
      }, {});
      setSelection(oldStateData);
    }
    updateItemsHandler();
    props.menuProps?.onDismiss && props.menuProps?.onDismiss(event, dismissAll);
  };

  const updateItemsHandler = useCallback(() => setItems(menuItems), [props.menuProps?.items]);

  const onEmptyDataRender = useCallback(
    () => (
      <Stack
        horizontal
        tokens={STYLES.EMPTY_DATA_STYLES.emptyDataTokens}
        style={STYLES.EMPTY_DATA_STYLES.emptyDataContainer}
      >
        <Icon iconName="SearchIssue" title="No data found" />
        <span>No data found</span>
      </Stack>
    ),
    []
  );

  const onSearchInputChange = useCallback(
    (_event?: ChangeEvent<HTMLInputElement>, newValue?: string) => {
      const filteredItems = menuItems.filter(
        (item) => item.text?.toLowerCase().indexOf((newValue ?? "").toLowerCase()) !== -1
      );

      if (!filteredItems.length) {
        filteredItems.push({
          key: "no_results"
        });
      }

      setItems(filteredItems);
    },
    [props.menuProps?.items]
  );

  const onSearchInputKeyDown = useCallback((event: any) => {
    /* Key Up, but we are not at the beginning of the text: stop event propagation to prevent ContextualMenu to focus */
    if (event.target.selectionStart > 0 && event.which === KeyCodes.up) {
      event.stopPropagation();
    }
    /* Key Down, but we are not at the end of the text: stop event propagation to prevent ContextualMenu to focus */
    if (event.target.selectionStart !== event.target.value.length && event.which === KeyCodes.down) {
      event.stopPropagation();
    }
  }, []);

  const renderMenuList = useCallback(
    (menuListProps?: IContextualMenuListProps, defaultRender?: IRenderFunction<IContextualMenuListProps>) => {
      const modifiedMenuListProps = contextualMenuItem.filter(({ key }) => !key).length
        ? ({
          ...menuListProps,
          hasCheckmarks: false,
          items: [
            {
              key: "no_results",
              onRender: onEmptyDataRender
            }
          ]
        } as IContextualMenuListProps)
        : menuListProps;

      if (props.menuProps?.onRenderMenuList) {
        props.menuProps?.onRenderMenuList(menuListProps, defaultRender);
      }

      // Check if all items are selected
      const allSelected = props.selectAllButton?.show &&
        props.selectAllButton?.selectValues?.length > 0 &&
        props.selectAllButton?.selectValues?.every((val: string) => selectedKeys?.includes(val));

      return (
        <>
          <div className={STYLES.SEARCH_BOX.searchBoxContainer}>
            <label className={STYLES.SEARCH_BOX.searchBoxLabel}>{props.name}</label>
            <SearchBox
              ariaLabel="Filter actions by text"
              placeholder="Search"
              onClear={updateItemsHandler}
              onChange={onSearchInputChange}
              onKeyDown={onSearchInputKeyDown}
              showIcon
            />
          </div>
          {/* Select All option at the top, styled like other checkbox items */}
          {props.selectAllButton?.show && (
            <div
              role="menuitemcheckbox"
              aria-checked={allSelected}
              onClick={() => {
                const selectValues = props.selectAllButton?.selectValues || [];
                if (selectValues.length > 0) {
                  if (allSelected) {
                    // If all selected, clear all
                    setSelection({});
                  } else {
                    // Select all
                    const newSelection = selectValues.reduce((acc: { [key: string]: boolean }, val: string) => {
                      acc[val] = true;
                      return acc;
                    }, {} as { [key: string]: boolean });
                    setSelection(newSelection);
                  }
                }
              }}
              style={{
                display: "flex",
                alignItems: "center",
                padding: "7px 12px",
                cursor: "pointer",
                borderBottom: "1px solid #edebe9",
                backgroundColor: "transparent"
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#f3f2f1"; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
            >
              <Icon
                iconName={allSelected ? "CheckboxComposite" : "Checkbox"}
                style={{
                  marginRight: 8,
                  fontSize: 16,
                  color: allSelected ? "#0078d4" : "#605e5c"
                }}
              />
              <span style={{ fontSize: 14, fontWeight: 600, color: "#323130" }}>
                {props.selectAllButton.label || "Select All"}
              </span>
            </div>
          )}
          {typeof defaultRender === "function" && defaultRender(modifiedMenuListProps)}
          <Stack
            className={STYLES.ACTION_BUTTONS.CLASS_NAMES.actionButtonsContainer}
            horizontal
            tokens={STYLES.ACTION_BUTTONS.CSS_STYLES.actionButtonsContainerTokens}
          >
            <PrimaryButton
              disabled={applyButtonDisabled === undefined || Boolean(applyButtonDisabled)}
              text="Apply"
              onClick={() => {
                const normalizedSelectedKeys = normalizeSingleSelection(selectedKeys);
                appliedFiltersRef.current = normalizedSelectedKeys;
                setAppliedFilters(normalizedSelectedKeys);
                containerRef.current?.click();

                // Update URL (either grouped or individual)
                updateUrlParams(normalizedSelectedKeys);

                props.onApply && props.onApply(normalizedSelectedKeys);
              }}
            />
            <DefaultButton
              text="Cancel"
              onClick={() => {
                containerRef.current?.click();
                // Restore to applied filters, or fallback to props.selectedKeys
                const restoreFilters = appliedFilters ?? (props.selectedKeys ? Object.keys(props.selectedKeys) : undefined);
                const oldStateData = restoreFilters?.reduce((previousValue: any, currentValue) => {
                  previousValue[currentValue] = true;
                  return previousValue;
                }, {});

                setSelection(oldStateData === undefined ? {} : oldStateData);
                props.onCancel && props.onCancel(appliedFilters);
              }}
            />
          </Stack>
          {props.clearAllButton?.show && (
            <div style={{ padding: "8px 12px", borderTop: "1px solid #edebe9", display: "flex", flexDirection: "column", gap: "8px" }}>
              <DefaultButton
                text={props.clearAllButton.label || "Clear All"}
                onClick={() => {
                  const clearToValue = props.clearAllButton?.clearToValue;
                  if (clearToValue && clearToValue.length > 0) {
                    const newSelection = clearToValue.reduce((acc: { [key: string]: boolean }, val: string) => {
                      acc[val] = true;
                      return acc;
                    }, {} as { [key: string]: boolean });
                    setSelection(newSelection);
                    appliedFiltersRef.current = clearToValue;
                    setAppliedFilters(clearToValue);
                  } else {
                    setSelection({});
                    appliedFiltersRef.current = undefined;
                    setAppliedFilters(undefined);
                  }
                  props.clearAllButton?.onClick();
                  containerRef.current?.click();
                }}
                styles={{
                  root: {
                    width: "100%",
                    border: "1px solid #0078d4",
                    color: "#0078d4"
                  },
                  rootHovered: {
                    backgroundColor: "#f3f9fd",
                    color: "#0078d4"
                  }
                }}
                iconProps={{ iconName: "ClearFilter" }}
              />
            </div>
          )}
        </>
      );
    },
    [
      contextualMenuItem,
      updateItemsHandler,
      onSearchInputChange,
      onSearchInputKeyDown,
      updateUrlParams,
      selectedKeys,
      appliedFilters,
      normalizeSingleSelection
    ]
  );

  const menuItemsForSelection = useMemo(() => {
    if (!props.singleSelect) return contextualMenuItem;

    return contextualMenuItem.map((item) => {
      if (!item.text || item.key === "no_results") return item;

      const originalOnClick = item.onClick;

      return {
        ...item,
        onClick: (event, menuItem) => {
          const value = (menuItem?.text ?? item.text)?.toString() ?? "";
          setSelection(value ? { [value]: true } : {});
          originalOnClick && originalOnClick(event, menuItem);
        }
      } as IContextualMenuItem;
    });
  }, [contextualMenuItem, props.singleSelect, setSelection]);

  const menuProps = useMemo(
    () =>
      ({
        ...props.menuProps,
        items: menuItemsForSelection,
        onDismiss: onDismissVerifyResetMenuItems,
        onRenderMenuList: renderMenuList
      }) as IContextualMenuProps,
    [menuItemsForSelection, renderMenuList]
  );

  useEffect(() => {
    if (!props.singleSelect || !selectedKeys || selectedKeys.length <= 1) return;
    const normalizedValues = normalizeSingleSelection(selectedKeys);
    const normalizedSelection = (normalizedValues ?? []).reduce(
      (acc, value) => {
        acc[value] = true;
        return acc;
      },
      {} as { [key: string]: boolean }
    );

    setSelection(normalizedSelection);
  }, [props.singleSelect, selectedKeys, normalizeSingleSelection, setSelection]);

  useEffect(() => {
    setItems(menuItems);
  }, [props.menuProps?.items]);

  useEffect(() => {
    // Only set initial selection if not already set from URL
    if (!props.urlParamKey || !searchParams.get(useGroupedFilters ? groupedFiltersParamName : props.urlParamKey)) {
      const selectedKeyValues = props.selectedKeys ? Object.keys(props.selectedKeys) : undefined;
      const normalizedValues = normalizeSingleSelection(selectedKeyValues);

      // Check if values actually changed to prevent infinite loops
      const currentApplied = appliedFiltersRef.current;
      const hasChanged = !currentApplied ||
        currentApplied.length !== (normalizedValues?.length ?? 0) ||
        !currentApplied.every((v, i) => v === normalizedValues?.[i]);

      if (hasChanged) {
        const normalizedSelection = (normalizedValues ?? []).reduce(
          (acc, value) => {
            acc[value] = true;
            return acc;
          },
          {} as { [key: string]: boolean }
        );

        setSelection(normalizedSelection);
        appliedFiltersRef.current = normalizedValues;
        setAppliedFilters(normalizedValues);
      }
    }
  }, [
    props.selectedKeys,
    props.urlParamKey,
    useGroupedFilters,
    groupedFiltersParamName,
    searchParams,
    normalizeSingleSelection
  ]);

  return (
    <div ref={containerRef}>
      <FilterPill
        {...props}
        menuProps={menuProps}
        resetProps={{
          ...props.resetProps,
          onClick: (event: any) => {
            setSelection({});
            appliedFiltersRef.current = undefined;
            setAppliedFilters(undefined);

            // Clear URL param (either grouped or individual)
            updateUrlParams(undefined);

            props.resetProps?.onClick && props.resetProps?.onClick(event);
            props.onResetFilters && props.onResetFilters(event);
          },
          "aria-label": props.resetProps?.["aria-label"] ?? `Clear ${props.name} filters`
        }}
        value={selectedKeys}
      />
    </div>
  );
};
